import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Toast from '../components/Toast';
import Logo from '../components/Logo';
import GoogleSignInButton from '../components/GoogleSignInButton';
import api from '../services/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [loading, setLoading] = useState(false);
  const { setUser, login: authLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setToast(null);
    setLoading(true);

    try {
      const payload: any = { email, password };
      if (requires2FA) {
        payload.twoFactorCode = twoFactorCode;
      }

      const response = await api.post('/auth/login', payload);
      
      // Check if 2FA is required
      if (response.data.data?.requires2FA) {
        setRequires2FA(true);
        setToast({ message: 'Bitte gib den Code aus deiner Email ein', type: 'info' });
        setLoading(false);
        return;
      }

      // Login successful - use AuthContext login to handle push tokens
      const { user, accessToken, refreshToken } = response.data.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      // Send pending push token after setting tokens
      console.log('🟡🟡🟡 [LOGIN-PAGE] Calling sendPendingPushToken...');
      try {
        // Import sendPendingPushToken logic here or trigger it
        const pendingToken = localStorage.getItem('pendingPushToken');
        const currentToken = localStorage.getItem('currentPushToken');
        const tokenToSend = pendingToken || currentToken;
        
        if (tokenToSend) {
          console.log('🟡 [LOGIN-PAGE] Sending token:', tokenToSend.substring(0, 30) + '...');
          await api.post('/auth/push-token', { 
            token: tokenToSend,
            platform: 'android',
            deviceInfo: navigator.userAgent
          });
          console.log('🟢 [LOGIN-PAGE] Token sent successfully');
          localStorage.removeItem('pendingPushToken');
          localStorage.setItem('currentPushToken', tokenToSend);
        } else {
          console.log('🟡 [LOGIN-PAGE] No token to send');
        }
      } catch (tokenError) {
        console.error('🔴 [LOGIN-PAGE] Error sending token:', tokenError);
      }
      
      setToast({ message: 'Erfolgreich angemeldet!', type: 'success' });
      
      setTimeout(() => {
        const redirect = localStorage.getItem('redirectAfterLogin');
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirect || '/');
      }, 500);
    } catch (err: any) {
      const errorData = err.response?.data;
      
      // Check if email verification is required
      if (errorData?.requiresVerification) {
        setToast({ message: 'Ein neuer Verifizierungscode wurde an deine Email gesendet', type: 'info' });
        setTimeout(() => {
          navigate('/verify-email', { state: { email } });
        }, 1500);
        return;
      }

      const message = errorData?.message || 'Login fehlgeschlagen';
      setToast({ message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backgroundColor: 'var(--bg-secondary)' }}>
      <div className="card" style={{ width: '100%', maxWidth: '400px' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ display: 'inline-block' }}>
            <Logo variant="dark" />
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              E-Mail
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={requires2FA}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Passwort
            </label>
            <input
              id="password"
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={requires2FA}
            />
          </div>

          {requires2FA && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--primary-light)', borderRadius: '0.5rem', border: '2px solid var(--primary)' }}>
              <label htmlFor="twoFactorCode" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                🔐 2FA-Code
              </label>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: '0.75rem' }}>
                Wir haben einen Code an deine Email gesendet
              </p>
              <input
                id="twoFactorCode"
                type="text"
                className="input"
                placeholder="123456"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                style={{ 
                  fontSize: '1.5rem', 
                  letterSpacing: '0.5rem', 
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}
                autoFocus
              />
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginBottom: '1rem' }}
            disabled={loading || (requires2FA && twoFactorCode.length !== 6)}
          >
            {loading ? 'Wird geladen...' : requires2FA ? 'Code bestätigen' : 'Anmelden'}
          </button>

          <p style={{ textAlign: 'center', color: 'var(--gray-600)' }}>
            Noch kein Konto?{' '}
            <Link to="/register" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: '500' }}>
              Registrieren
            </Link>
          </p>
        </form>

        {/* Google Sign-In */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '1.25rem 0' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--gray-200)' }} />
          <span style={{ color: 'var(--gray-400)', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>oder</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--gray-200)' }} />
        </div>

        <GoogleSignInButton
          onSuccess={() => {
            const redirect = localStorage.getItem('redirectAfterLogin');
            localStorage.removeItem('redirectAfterLogin');
            navigate(redirect || '/');
          }}
          onError={(msg) => setToast({ message: msg, type: 'error' })}
        />
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
